import type { Coupon } from '@monorepo/shared'
import { COUPON_STATUS, COUPON_TYPE } from '@monorepo/shared/schemas/coupons'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Archive,
  DollarSign,
  Edit,
  MoreHorizontal,
  Pause,
  Percent,
  Play,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useDeleteCoupon, useUpdateCoupon } from '../hooks/useCoupons'

interface CouponsTableProps {
  coupons: Coupon[]
  onEditCoupon?: (coupon: Coupon) => void
  onViewCoupon?: (coupon: Coupon) => void
}

export function CouponsTable({
  coupons,
  onEditCoupon,
  onViewCoupon,
}: CouponsTableProps) {
  const [deleteCoupon, setDeleteCoupon] = useState<Coupon | null>(null)
  const deleteCouponMutation = useDeleteCoupon()
  const updateCouponMutation = useUpdateCoupon()

  const handleCouponAction = async (action: string, coupon: Coupon) => {
    switch (action) {
      case 'view': {
        onViewCoupon?.(coupon)
        break
      }
      case 'edit': {
        onEditCoupon?.(coupon)
        break
      }
      case 'activate': {
        try {
          await updateCouponMutation.mutateAsync({
            id: coupon.id,
            data: { status: COUPON_STATUS.active },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      }
      case 'deactivate': {
        try {
          await updateCouponMutation.mutateAsync({
            id: coupon.id,
            data: { status: COUPON_STATUS.draft },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      }
      case 'archive': {
        try {
          await updateCouponMutation.mutateAsync({
            id: coupon.id,
            data: { status: COUPON_STATUS.archived },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      }
      case 'delete': {
        setDeleteCoupon(coupon)
        break
      }
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteCoupon) {
      try {
        await deleteCouponMutation.mutateAsync(deleteCoupon.id)
        setDeleteCoupon(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }

  const columns: ColumnDef<Coupon>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => {
        const coupon = row.original
        return (
          <div className='font-mono font-medium text-sm'>{coupon.code}</div>
        )
      },
      size: 150,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const coupon = row.original
        return <div className='font-medium'>{coupon.name}</div>
      },
      size: 200,
    },
    {
      accessorKey: 'coupon_type',
      header: 'Type',
      cell: ({ row }) => {
        const coupon = row.original
        return (
          <div className='flex items-center gap-2'>
            {coupon.coupon_type === COUPON_TYPE.percentage ? (
              <Percent className='w-4 h-4 text-blue-600' />
            ) : (
              <DollarSign className='w-4 h-4 text-green-600' />
            )}
            <span className='capitalize'>
              {coupon.coupon_type.replace('_', ' ')}
            </span>
          </div>
        )
      },
      size: 140,
    },
    {
      accessorKey: 'discount',
      header: 'Discount',
      cell: ({ row }) => {
        const coupon = row.original
        if (coupon.coupon_type === COUPON_TYPE.percentage) {
          return (
            <div className='font-medium text-blue-600'>
              {coupon.percentage_rate}%
            </div>
          )
        } else {
          return (
            <div className='font-medium text-green-600'>
              {coupon.currency} {(coupon.amount_cents! / 100).toFixed(2)}
            </div>
          )
        }
      },
      size: 120,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const coupon = row.original
        return (
          <Badge
            variant={
              coupon.status === COUPON_STATUS.active
                ? 'default'
                : coupon.status === COUPON_STATUS.draft
                  ? 'secondary'
                  : 'outline'
            }
            className='text-xs capitalize'
          >
            {coupon.status}
          </Badge>
        )
      },
      size: 100,
    },
    {
      accessorKey: 'redemptions',
      header: 'Redemptions',
      cell: ({ row }) => {
        const coupon = row.original
        const redeemed = coupon.times_redeemed || 0
        const max = coupon.max_redemptions
        return (
          <div className='text-center font-medium'>
            <span>{redeemed}</span>
            {max && <span className='text-muted-foreground'> / {max}</span>}
            {!max && <span className='text-muted-foreground'> / ∞</span>}
          </div>
        )
      },
      size: 130,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const coupon = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm'>
                <MoreHorizontal className='w-4 h-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleCouponAction('edit', coupon)
                }}
              >
                <Edit className='w-4 h-4 mr-2' />
                Edit
              </DropdownMenuItem>
              {coupon.status === COUPON_STATUS.draft && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleCouponAction('activate', coupon)
                  }}
                >
                  <Play className='w-4 h-4 mr-2' />
                  Activate
                </DropdownMenuItem>
              )}
              {coupon.status === COUPON_STATUS.active && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleCouponAction('deactivate', coupon)
                  }}
                >
                  <Pause className='w-4 h-4 mr-2' />
                  Make Draft
                </DropdownMenuItem>
              )}
              {coupon.status !== COUPON_STATUS.archived && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleCouponAction('archive', coupon)
                  }}
                >
                  <Archive className='w-4 h-4 mr-2' />
                  Archive
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleCouponAction('delete', coupon)
                }}
                className='text-red-600 focus:text-red-600'
              >
                <Trash2 className='w-4 h-4 mr-2' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 50,
    },
  ]

  return (
    <>
      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <EnhancedTable
          key='coupons-table'
          data={coupons}
          columns={columns}
          height='calc(100vh - 400px)'
          className='bg-transparent border-0'
          onRowClick={coupon => onViewCoupon?.(coupon)}
        />
      </div>

      <ConfirmationDialog
        open={!!deleteCoupon}
        onOpenChange={open => !open && setDeleteCoupon(null)}
        title='Delete Coupon'
        description={`Are you sure you want to delete "${deleteCoupon?.name}" (${deleteCoupon?.code})? This action cannot be undone.`}
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        isLoading={deleteCouponMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteCoupon(null)}
      />
    </>
  )
}
